import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { IconCarCrash } from '@tabler/icons-react';

export default function IncidentList() {
  const navigate = useNavigate();
  const { orgId, userProfile } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  const resolvedOrgId = orgId || userProfile?.orgId;

  useEffect(() => {
    const load = async () => {
      if (!resolvedOrgId) {
        setLoading(false);
        return;
      }
      try {
        const snap = await getDocs(
          query(
            collection(db, 'organisations', resolvedOrgId, 'incidents'),
            orderBy('detectedAt', 'desc')
          )
        );
        setIncidents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [resolvedOrgId]);

  const open = incidents.filter(i => i.status === 'open').length;
  const review = incidents.filter(i => i.status === 'review').length;
  const closed = incidents.filter(i => i.status === 'closed').length;

  const severityColour = s => s === 'high' ? 'var(--color-danger)' : s === 'medium' ? 'var(--color-warning)' : '#FAC775';
  const statusStyle = s => s === 'open'
    ? { bg: 'var(--color-danger-light)', color: 'var(--color-danger)' }
    : s === 'review'
      ? { bg: 'var(--color-warning-light)', color: 'var(--color-warning)' }
      : { bg: 'var(--color-success-light)', color: 'var(--color-success)' };

  const formatTime = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return `Today ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const getInitials = name => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const avatarColours = [
    { bg: '#E6F1FB', color: '#185FA5' },
    { bg: '#EAF3DE', color: '#3B6D11' },
    { bg: '#EEEDFE', color: '#534AB7' },
    { bg: '#FCEBEB', color: '#A32D2D' },
    { bg: '#FDF0E8', color: '#993C1D' },
    { bg: '#E1F5EE', color: '#085041' },
  ];

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Incidents</h1>
            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              All branches · {loading ? '...' : `${incidents.length} total`}
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total', value: incidents.length, colour: 'var(--color-danger)' },
            { label: 'Open', value: open, colour: 'var(--color-danger)' },
            { label: 'Under review', value: review, colour: 'var(--color-warning)' },
            { label: 'Closed', value: closed, colour: 'var(--color-success)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '16px 18px' }}>
              <div style={{ fontSize: 28, fontWeight: 400, color: s.colour, marginBottom: 4 }}>
                {loading ? '—' : s.value}
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '16px 1.6fr 1.2fr 0.8fr 0.6fr 0.6fr 0.6fr 60px', gap: 0, padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)', alignItems: 'center' }}>
            {['', 'Driver & vehicle', 'Branch · location', 'Date & time', 'G-force', 'Severity', 'Status', ''].map((h, i) => (
              <div key={i} style={{ fontSize: 10, fontWeight: 400, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading incidents...</div>
          ) : incidents.length === 0 ? (
            <div style={{ padding: '48px 32px', textAlign: 'center' }}>
              <IconCarCrash size={32} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 6 }}>No incidents recorded</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>Incidents are detected automatically when crash detection is enabled</div>
            </div>
          ) : incidents.map((inc, i) => {
            const colours = avatarColours[i % avatarColours.length];
            return (
              <div
                key={inc.id}
                onClick={() => navigate(`/incidents/${inc.id}`)}
                style={{ display: 'grid', gridTemplateColumns: '16px 1.6fr 1.2fr 0.8fr 0.6fr 0.6fr 0.6fr 60px', gap: 0, padding: '13px 16px', borderBottom: i < incidents.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', cursor: 'pointer', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: severityColour(inc.severity) }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: colours.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: colours.color, flexShrink: 0 }}>
                    {getInitials(inc.driverName)}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-text-primary)' }}>{inc.driverName || '—'}</div>
                    <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      {inc.assetReg && <span style={{ fontFamily: 'monospace', fontSize: 9, background: '#FAC775', color: '#412402', padding: '0 4px', borderRadius: 2 }}>{inc.assetReg}</span>}
                      {inc.assetName}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  {inc.branchId}{inc.location ? ` · ${inc.location}` : ''}
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{formatTime(inc.detectedAt)}</div>
                <div style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--color-text-secondary)' }}>
                  {inc.gforce ? `${inc.gforce}G` : '—'}
                </div>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: severityColour(inc.severity) + '20', color: severityColour(inc.severity), width: 'fit-content' }}>
                  {inc.severity ? inc.severity.charAt(0).toUpperCase() + inc.severity.slice(1) : '—'}
                </span>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: statusStyle(inc.status || 'open').bg, color: statusStyle(inc.status || 'open').color, width: 'fit-content' }}>
                  {inc.status ? inc.status.charAt(0).toUpperCase() + inc.status.slice(1) : 'Open'}
                </span>
                <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>View</div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}